/**
 * Query keys, in one place.
 *
 * Every key starts with its domain, so `invalidateQueries({ queryKey: qk.games.all })`
 * reaches every games query without naming them. This is the whole point of the
 * exercise: `useSupabaseFantasyData` had one `refreshData()` that refetched
 * seasons, teams, games, rosters, divisions and standings after *any* mutation,
 * behind a full-screen overlay. A mutation should invalidate what it changed.
 *
 * Keys are arrays, ordered general → specific. Never build one inline in a
 * component; add it here so the invalidation side can find it.
 */

const scope = (domain) => ({
  all: [domain],
  /** Everything under one season. */
  season: (seasonId) => [domain, seasonId]
});

export const qk = {
  seasons: {
    all: ['seasons'],
    list: () => ['seasons', 'list'],
    active: () => ['seasons', 'active'],
    detail: (seasonId) => ['seasons', 'detail', seasonId]
  },

  teams: {
    ...scope('teams'),
    forSeason: (seasonId) => ['teams', seasonId, 'list'],
    faabLedger: (seasonId) => ['teams', seasonId, 'faab']
  },

  divisions: {
    ...scope('divisions'),
    forSeason: (seasonId) => ['divisions', seasonId, 'list'],
    standings: (seasonId) => ['divisions', seasonId, 'standings']
  },

  games: {
    ...scope('games'),
    forSeason: (seasonId) => ['games', seasonId, 'list'],
    forWeek: (seasonId, week) => ['games', seasonId, 'week', week],
    completed: (seasonId, upToWeek = null) => ['games', seasonId, 'completed', upToWeek],
    completedWeeks: (seasonId) => ['games', seasonId, 'completedWeeks'],
    currentWeek: (seasonId) => ['games', seasonId, 'currentWeek']
  },

  rosters: {
    ...scope('rosters'),
    forSeason: (seasonId) => ['rosters', seasonId, 'all'],
    forTeam: (teamId) => ['rosters', 'team', teamId],
    stats: (seasonId) => ['rosters', seasonId, 'stats'],
    /**
     * Current lineups, with a week's points layered on. Under `rosters` rather
     * than `playerStats` because the roster snapshot is what makes it stale or
     * fresh — a roster sync must invalidate this, and a prefix invalidation of
     * `rosters` is how that happens.
     */
    lineupsForWeek: (seasonId, week) => ['rosters', seasonId, 'lineups', week]
  },

  players: {
    all: ['players'],
    forSeason: (seasonId) => ['players', seasonId],
    /**
     * The parlay autocomplete. Keyed on the *debounced* term, so the cache
     * holds one entry per query the user actually paused on rather than one
     * per keystroke.
     */
    search: (query) => ['players', 'search', query],
    /** Exact names, sorted, for the chat's data tables (`usePlayersByName`). */
    byNames: (names) => ['players', 'byNames', names]
  },

  /**
   * Per-player, per-week scoring. Written out of process by the weekly sync, so
   * nothing in the app invalidates these — there is no mutation to hang an
   * `onSuccess` on. `useRankingsForWeek` needs no key of its own here: player
   * stats are an input of that query's own `queryFn`, not a separate cache
   * entry it composes.
   */
  /**
   * The week in progress, from the shared snapshot `live-scores` maintains.
   * Volatile, and nothing invalidates it: the hook polls while a game is on.
   */
  live: {
    ...scope('live'),
    week: (seasonId, week) => ['live', seasonId, 'week', week]
  },

  playerStats: {
    ...scope('playerStats'),
    forSeason: (seasonId, throughWeek = null) =>
      ['playerStats', seasonId, 'season', throughWeek],
    forTeam: (seasonId, teamId) => ['playerStats', seasonId, 'team', teamId],
    /**
     * One week, inclusive — a different question from `forSeason`, whose
     * `throughWeek` is exclusive. Separate keys because they are separate
     * queries; sharing one would make "weeks 1-3" and "week 3" collide.
     */
    forWeek: (seasonId, week) => ['playerStats', seasonId, 'week', week]
  },

  rankings: {
    ...scope('rankings'),
    /**
     * Rankings depend on the week being viewed *and* the week the league is
     * actually in — viewing week 3 in week 9 is a different calculation from
     * viewing week 3 live. Both belong in the key or the cache lies.
     */
    forViewedWeek: (seasonId, week, currentWeek) =>
      ['rankings', seasonId, 'viewed', week, currentWeek],
    history: (seasonId, week = null) => ['rankings', seasonId, 'history', week],
    snapshotWeeks: (seasonId) => ['rankings', seasonId, 'snapshotWeeks']
  },

  pickems: {
    ...scope('pickems'),
    week: (seasonId, week) => ['pickems', seasonId, 'week', week],
    allWeeks: (seasonId) => ['pickems', seasonId, 'weeks'],
    status: (seasonId) => ['pickems', seasonId, 'status'],
    gameData: (seasonId, week) => ['pickems', seasonId, 'gameData', week],
    allSeasonPicks: (seasonId) => ['pickems', seasonId, 'allSeasonPicks'],
    userPicks: (pickEmWeekId, userId = null) => ['pickems', 'userPicks', pickEmWeekId, userId],
    allPicks: (pickEmWeekId) => ['pickems', 'allPicks', pickEmWeekId],
    scores: (pickEmWeekId) => ['pickems', 'scores', pickEmWeekId]
  },

  /**
   * The weekly TD parlay. `season` is the dashboard's whole-season read;
   * `weekPicks` is the league's picks for one week — a genuinely different
   * cache entry from `myPick`, which is one row and is keyed on the viewer, so
   * neither can be derived from the other.
   */
  parlay: {
    ...scope('parlay'),
    season: (seasonId) => ['parlay', seasonId, 'season'],
    myPick: (pickEmWeekId, userId = null) => ['parlay', 'myPick', pickEmWeekId, userId],
    weekPicks: (pickEmWeekId) => ['parlay', 'weekPicks', pickEmWeekId],
    /**
     * The week's live TD status, from ESPN's public API. Volatile and
     * client-polled — `useParlayLive` owns its own interval and never touches
     * `scored_td`, so no mutation invalidates this; it simply refetches while a
     * game is live and stops when none is.
     */
    live: (pickEmWeekId) => ['parlay', 'live', pickEmWeekId]
  },

  /**
   * What the *viewer* is allowed to do, as opposed to what the league contains.
   * Keyed by user id so signing in as somebody else cannot read a cached yes.
   */
  viewer: {
    all: ['viewer'],
    parlayCommissioner: (userId) => ['viewer', 'parlayCommissioner', userId],
    approved: (userId) => ['viewer', 'approved', userId],
    /** Is this viewer a league admin — a `league_admins` row, asked of
     *  `is_admin()`. Per user id like the two above. */
    admin: (userId) => ['viewer', 'admin', userId],
    /**
     * This device's push notification state: what the browser holds and what
     * the server has stored for it. Per device, not just per user — a member
     * can have notifications on for their phone and off for their laptop —
     * but the endpoint is not known until the query has asked the browser, so
     * the key carries the user and the query answers for this device.
     */
    push: (userId) => ['viewer', 'push', userId],
    /**
     * The viewer's Alerts: every notification they were sent
     * (`notification_inbox`). The bell's badge and the Alerts page read
     * the same entry, so the count and the list cannot disagree.
     */
    inbox: (userId) => ['viewer', 'inbox', userId],
    /** The viewer's taunt offers (`matchup_taunts`), read beside Alerts. */
    taunts: (userId) => ['viewer', 'taunts', userId],
    /** The franchise this account may edit (`franchise_accounts`). */
    franchise: (userId) => ['viewer', 'franchise', userId]
  },

  /**
   * Every franchise's customized profile, and the admin's list of which
   * account speaks for which. One league-wide entry: the avatar on every team
   * chip reads it, so a saved profile reaches them all by one invalidation.
   */
  profiles: {
    all: ['profiles'],
    list: () => ['profiles', 'list'],
    accounts: () => ['profiles', 'accounts']
  },

  /**
   * Role administration. Separate from `viewer` on purpose: `viewer` answers
   * "what may *I* do" and is cached per user, while these answer "who holds
   * what" and are admin-only reads. Granting a role invalidates both, because
   * the grantee's own answer has changed too.
   */
  roles: {
    all: ['roles'],
    members: () => ['roles', 'members'],
    holders: (role) => ['roles', 'holders', role]
  },

  /**
   * The viewer's own autosubs. Under its own domain, keyed general to specific,
   * so a set or a remove invalidates the week it changed and nothing else.
   */
  autosubs: {
    ...scope('autosubs'),
    forWeek: (seasonId, week) => ['autosubs', seasonId, 'week', week]
  },

  /**
   * The approval queue. Beside `roles` for the same reason `roles` is beside
   * `viewer`: "who is waiting" is an admin read, "am I approved" is the
   * viewer's own answer under `viewer.approved`. Deciding a request
   * invalidates both, plus `roles` — the member list is drawn from the same
   * accounts.
   */
  approvals: {
    all: ['approvals'],
    list: () => ['approvals', 'list']
  },

  /**
   * Who the league's admins are, for Settings → Admins. An admin read beside
   * `roles` and `approvals`; "am I an admin" is the viewer's own answer under
   * `viewer.admin`. A grant or revoke invalidates this list and the affected
   * user's `viewer.admin` entry.
   */
  admins: {
    all: ['admins'],
    list: () => ['admins', 'list']
  },

  /**
   * `results` sits under `awards.season(seasonId)`, so the invalidations in
   * `useAwardsMutations` reach it and a submitted vote refreshes the charts.
   * `ballotSeasons` deliberately does not: the set of seasons that have a
   * ballot changes once a year, not once a vote.
   */
  awards: {
    ...scope('awards'),
    unlockStatus: (seasonId) => ['awards', seasonId, 'unlockStatus'],
    results: (seasonId) => ['awards', seasonId, 'results'],
    ballotSeasons: () => ['awards', 'ballotSeasons']
  },

  /**
   * The predictions board. Every key starts `['takes', seasonId, …]` so a
   * mutation can invalidate the whole domain with `qk.takes.season(seasonId)`
   * and reach all of it — deliberately unlike `pickems.userPicks`, which is
   * keyed `['pickems', 'userPicks', …]` and so is missed by exactly that
   * prefix.
   *
   * The detail sheet reads its *take* out of the board entry by id rather than
   * fetching it again — but its activity log is keyed per take, because that is
   * the one part of a take nothing else on the page displays and so the one
   * part worth deferring until somebody opens it.
   *
   * `seen` is the deliberate exception to the `seasonId` shape, and it is
   * `pickems.userPicks`' arrangement for the same reason: a member's read mark
   * (`take_views`) belongs to the person rather than to a season, and it must
   * *not* be swept up by the mutations' `qk.takes.season(seasonId)` — fading a
   * take does not change when you last read the board. `['takes', 'seen', …]`
   * misses that prefix because `'seen'` is not a season id, which is exactly
   * the property being relied on.
   */
  takes: {
    ...scope('takes'),
    board: (seasonId) => ['takes', seasonId, 'board'],
    activity: (seasonId, takeId) => ['takes', seasonId, 'activity', takeId],
    seen: (userId) => ['takes', 'seen', userId]
  },

  /**
   * League history. Every entry spans seasons rather than living under one, so
   * this domain is keyed by what is being asked rather than by `seasonId`, and
   * the whole of it is invalidated when a season is finalized.
   */
  history: {
    all: ['history'],
    timeline: () => ['history', 'timeline'],
    franchises: () => ['history', 'franchises'],
    championships: () => ['history', 'championships'],
    seasonDetail: (seasonId) => ['history', 'season', seasonId],
    h2hMatrix: () => ['history', 'h2h', 'matrix'],
    matchup: (franchise1Id, franchise2Id) => ['history', 'h2h', franchise1Id, franchise2Id],
    recordBook: () => ['history', 'records'],
    divisionTitles: () => ['history', 'divisionTitles'],
    recordTrends: () => ['history', 'recordTrends'],
    franchiseProfile: (franchiseId) => ['history', 'franchise', franchiseId],
    transactionLeaderboard: () => ['history', 'transactions'],
    franchiseTransactions: (franchiseId) => ['history', 'transactions', franchiseId],
    /**
     * One season, every franchise, week by week — the franchise profile's
     * week view. League-wide on purpose: switching teams inside a season
     * fetches nothing, and a player's weekly rank is a league comparison.
     */
    seasonWeeks: (seasonId) => ['history', 'seasonWeeks', seasonId],
    /** Every week one player was rostered in the league, across seasons. */
    playerCareer: (playerId) => ['history', 'player', playerId]
  },

  /**
   * The automation dashboard. `runs` is the `sync_runs` log; `health` is what
   * the jobs left in the tables they own. Both are admin reads of data the
   * cron writes out of process, so nothing in the app invalidates them —
   * they refetch on focus and on a timer instead.
   */
  automations: {
    all: ['automations'],
    runs: (seasonId = null, limit = 40) => ['automations', 'runs', seasonId, limit],
    health: (seasonId, seasonYear, throughWeek) =>
      ['automations', 'health', seasonId, seasonYear, throughWeek]
  },

  schedule: {
    all: ['schedule'],
    /** The ESPN import log. Written by scripts, read-only in the app. */
    history: (limit = 25) => ['schedule', 'history', limit]
  },

  /**
   * The NFL calendar — who each pro team plays in each week.
   *
   * Named `nflSchedule` rather than `schedule` because `schedule` above is
   * already this app's ESPN *import log*, and a domain prefix that meant two
   * things would make a prefix invalidation reach the wrong one.
   *
   * Keyed by `seasonYear` and not by `seasonId`, the only key here that is:
   * the NFL's calendar is league-independent, so the table has no season FK
   * and neither does its key. See `services/db/nflSchedule.js`.
   *
   * One entry per season and no finer. A week's opponents and a team's bye are
   * `select` projections of this same entry rather than keys of their own —
   * a season is ~576 rows, and deriving every view from one cached fetch is
   * what stops two of them disagreeing about the same week. Written by the
   * weekly cron, so there is no mutation to invalidate it.
   */
  nflSchedule: {
    all: ['nflSchedule'],
    season: (seasonYear) => ['nflSchedule', seasonYear]
  }
};

export default qk;
